import React from 'react';
import { useHostInfo } from './useHostInfo';
import { APP_VERSION } from '../utils/version';
import { NavLink, Outlet } from 'react-router-dom';
import NewEventModal from './NewEventModal';

// Desktop shell: top bar + collapsible side nav (a slide-in drawer takes over
// below 880px in a narrow browser window — CSS handles the switch).
export default function LayoutDesktop({ vm }) {
  const {
    user, roleLabel, isSuperAdmin, isViewingAs, initials, fullName, shellClass,
    visibleNav, navIcon, events, event, onEventChange,
    drawerOpen, setDrawerOpen, theme, toggleTheme,
    viewAsOptions, onViewAsChange, returnToSuperAdmin,
    eventModalOpen, openEventModal, doLogout, forgetDevice,
  } = vm;

  // The menu is hidden; ☰ slides it in OVER the page (any window width), and
  // it closes again on ☰, a click outside, Esc / back, or choosing a page —
  // so every page gets the full width.
  const toggleNav = () => setDrawerOpen(o => !o);
  const host = useHostInfo();
  const closeDrawer = () => setDrawerOpen(false);

  const NavBody = () => (
    <>
      <NavLink to="/app/profile" className="navhead" onClick={closeDrawer}>
        <div className="avatar">{initials}</div>
        <div style={{ minWidth: 0 }}>
          <div className="navhead-name">{fullName}</div>
          <div className="navhead-sub">#{user?.UserId} - {user?.MobileNumber || '-'}</div>
          <div className="navhead-sub">{roleLabel}</div>
        </div>
      </NavLink>
      {visibleNav.map(n => (
        <NavLink key={n.to} to={n.to} onClick={closeDrawer} className={({ isActive }) => isActive ? 'active' : ''}>
          <span className="ic">{navIcon(n)}</span>
          {n.label}
        </NavLink>
      ))}
      <div style={{ flex: 1 }} />
      <button type="button" className="navlink-btn" onClick={() => { toggleTheme(); closeDrawer(); }}>
        {theme === 'dark' ? '☀ Light Mode' : '🌙 Dark Mode'}
      </button>
      <NavLink to="/app/profile" onClick={closeDrawer} className={({ isActive }) => isActive ? 'active' : ''}>Profile</NavLink>
      <button type="button" className="navlink-btn" onClick={() => { closeDrawer(); forgetDevice(); }}>Forget device</button>
      <button type="button" className="navlink-btn danger" onClick={() => { closeDrawer(); doLogout(); }}>Logout</button>
    </>
  );

  return (
    <div className={shellClass}>
      <div className="topbar">
        <button className="iconbtn hamburger" onClick={toggleNav} title={drawerOpen ? 'Close menu' : 'Open menu'} aria-label="Menu">☰</button>
        <span className="brand">Mehfil by Smartify</span>
        <div className="topbar-pickers">
          <select value={event?.EventId ?? ''} onChange={(e) => onEventChange(e.target.value)} title="Event">
            <option value="">Select event</option>
            {events.map(e => <option key={e.EventId} value={e.EventId}>{e.EventName}</option>)}
          </select>
        </div>
        {isSuperAdmin && <button className="iconbtn add-event" onClick={openEventModal} title="Add event">+</button>}
        <span className="spacer" />
        {isSuperAdmin && (
          <select className="view-as-select" value="" onChange={(e) => onViewAsChange(e.target.value)} title="View app as another user">
            <option value="">View as user</option>
            {viewAsOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
        )}
        {isViewingAs && (
          <button type="button" className="btn light sm view-as-return" onClick={returnToSuperAdmin}>Return to Super Admin</button>
        )}
        {/* The app version (always) and, in development, the address other
            devices on the same Wi-Fi open. */}
        <div className="topbar-hosts">
          {host.local && (
            <a href={host.local} target="_blank" rel="noreferrer" title="Open this on another laptop / phone on the same Wi-Fi">
              {host.local.replace('http://', '')}
            </a>
          )}
          {host.local && ' · '}
          <span title={`Mehfil by Smartify ${APP_VERSION}`}>v{APP_VERSION}</span>
        </div>
        <span className="topbar-user">{user?.Name} - {roleLabel}</span>
      </div>

      <div className="body-wrap">
        {/* Kept for layout, never shown: the menu is the slide-in drawer below. */}
        <nav className="sidenav" aria-hidden="true" />
        <main className="content"><Outlet /></main>
      </div>

      {drawerOpen && (
        <div className="drawer-back" onClick={closeDrawer}>
          <nav className="drawer" onClick={(e) => e.stopPropagation()}><NavBody /></nav>
        </div>
      )}

      {eventModalOpen && <NewEventModal vm={vm} />}
    </div>
  );
}
